import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function AnalyticsPanel() {
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api
      .get("/admin/analytics")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Errore nel caricamento delle analytics"));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) return <div className="font-mono text-xs text-slate-400 uppercase tracking-widest">Caricamento…</div>;

  const max = Math.max(...data.days.map((d) => d.count), 1);

  return (
    <div data-testid="analytics-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display font-extrabold text-xl tracking-tight text-ink">Analytics</h2>
          <p className="text-sm text-slate-500 mt-0.5">Aperture del link di condivisione.</p>
        </div>
        <button
          data-testid="analytics-refresh-button"
          onClick={load}
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-ink transition-colors"
        >
          <RefreshCw size={14} />
          Aggiorna
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-line rounded-xl p-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Visualizzazioni totali</div>
          <div className="mt-2 font-display font-extrabold text-4xl tracking-tight text-ink" data-testid="analytics-total">
            {data.total}
          </div>
        </div>
        <div className="bg-white border border-line rounded-xl p-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Ultimi 7 giorni</div>
          <div className="mt-2 font-display font-extrabold text-4xl tracking-tight text-ink">
            {data.days.slice(-7).reduce((a, d) => a + d.count, 0)}
          </div>
        </div>
        <div className="bg-white border border-line rounded-xl p-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Oggi</div>
          <div className="mt-2 font-display font-extrabold text-4xl tracking-tight text-brand">
            {data.days[data.days.length - 1]?.count ?? 0}
          </div>
        </div>
      </div>

      <div className="bg-white border border-line rounded-xl p-5 mb-6">
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-4">Ultimi 30 giorni</div>
        <div className="flex items-end gap-[3px] h-28" data-testid="analytics-chart">
          {data.days.map((d) => (
            <div
              key={d.date}
              title={`${d.date}: ${d.count} visite`}
              className="flex-1 bg-brand/80 rounded-t-sm hover:bg-brand transition-colors"
              style={{ height: `${Math.max((d.count / max) * 100, d.count > 0 ? 6 : 1.5)}%` }}
            />
          ))}
        </div>
      </div>

      <div className="bg-white border border-line rounded-xl p-5">
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-3">Visite recenti</div>
        {data.recent.length === 0 ? (
          <p className="text-sm text-slate-400">Nessuna visita registrata.</p>
        ) : (
          <div className="divide-y divide-line">
            {data.recent.map((v, i) => (
              <div key={i} className="py-2.5 flex items-center justify-between gap-4 text-sm">
                <span className="font-mono text-xs text-slate-500 shrink-0">
                  {new Date(v.ts).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                </span>
                <span className="text-slate-600 truncate">{v.ref || "Visita diretta"}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
